import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Package, MapPin, Lock, Settings, ChevronLeft, CheckCircle, XCircle, Clock, Eye, EyeOff, User, Mail, Phone, RotateCcw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { userService } from '../services/userService';
import { orderService } from '../services/orderService';
import toast from 'react-hot-toast';
import LocateViaLiveMap from './LocateViaLiveMap';



// My Orders Component
export const MyOrdersContent = ({ onBack }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [returnReason, setReturnReason] = useState('');
  const [returnLoading, setReturnLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await orderService.getUserOrders();
      setOrders(response?.data || []);
    } catch (error) {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Delivered': return <CheckCircle className="text-green-500" size={13} />;
      case 'Cancelled': return <XCircle className="text-red-500" size={13} />;
      default: return <Clock className="text-amber-500" size={13} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Delivered': return 'bg-green-50 text-green-600 border border-green-200';
      case 'Cancelled': return 'bg-red-50 text-red-600 border border-red-200';
      default: return 'bg-amber-50 text-amber-600 border border-amber-200';
    }
  };

  const formatDate = (date) => new Date(date).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
  const formatPrice = (price) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(price);

  const handleBack = () => {
    if (onBack) onBack();
    else navigate('/store');
  };

  const toggleOrderDetails = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  const handleReturnClick = (order) => {
    setSelectedOrder(order);
    setShowReturnModal(true);
  };

  const handleReturnSubmit = async (e) => {
    e.preventDefault();
    if (!returnReason.trim()) {
      toast.error('Please provide a reason for return');
      return;
    }

    setReturnLoading(true);
    try {
      await orderService.requestReturn(selectedOrder._id, returnReason);
      toast.success('Return request submitted successfully!');
      setShowReturnModal(false);
      setReturnReason('');
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to submit return request');
    } finally {
      setReturnLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="text-purple-600 font-medium text-xs tracking-wide">Loading...</div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto pb-16 bg-white">
      {/* Header */}
      <div className="sticky top-0 bg-white/90 backdrop-blur-sm border-b border-gray-100 px-3 py-2 z-10">
        <div className="flex items-center gap-2">
          <button onClick={handleBack} className="p-1.5 hover:bg-gray-50 rounded-md transition-colors">
            <ChevronLeft size={16} className="text-gray-600" />
          </button>
          <Package size={16} className="text-purple-600" />
          <h2 className="font-semibold text-sm text-gray-800">My Orders</h2>
        </div>
      </div>

      <div className="p-3 space-y-2.5">
        {orders.length === 0 ? (
          <div className="text-center py-6">
            <Package size={36} className="mx-auto text-gray-300 mb-2" />
            <p className="text-xs text-gray-400">No orders yet</p>
          </div>
        ) : (
          orders.map((order) => (
            <div key={order._id} className="border border-gray-100 rounded-lg overflow-hidden bg-white shadow-sm shadow-gray-50">
              {/* Order Header */}
              <div className="p-2.5 bg-gray-50/50 flex justify-between items-center border-b border-gray-100">
                <div>
                  <div className="text-[10px] font-mono text-gray-400">#{order._id.slice(-8).toUpperCase()}</div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    {getStatusIcon(order.status)}
                    <span className="text-[11px] text-gray-500 font-medium">{formatDate(order.createdAt)}</span>
                  </div>
                </div>
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium tracking-wide ${getStatusColor(order.status)}`}>
                  {order.status || 'Processing'}
                </span>
              </div>

              {/* Order Items Preview */}
              <div className="p-2.5">
                <div className="flex gap-1.5 mb-2">
                  {order.orderItems?.slice(0, 4).map((item, i) => (
                    <div key={i} className="w-10 h-10 border border-gray-100 rounded bg-white overflow-hidden flex-shrink-0">
                      <img src={item.image || 'https://via.placeholder.com/40'} alt="" className="w-full h-full object-contain" />
                    </div>
                  ))}
                  {order.orderItems?.length > 4 && (
                    <div className="w-10 h-10 bg-gray-50 border border-gray-100 rounded flex items-center justify-center flex-shrink-0">
                      <span className="text-[10px] font-semibold text-gray-500">+{order.orderItems.length - 4}</span>
                    </div>
                  )}
                </div>
                
                <div className="flex justify-between items-center text-xs mb-2">
                  <span className="text-gray-400 text-[11px]">{order.orderItems?.length} {order.orderItems?.length === 1 ? 'item' : 'items'}</span>
                  <span className="font-semibold text-gray-800">{formatPrice(order.totalPrice)}</span>
                </div>

                <button
                  onClick={() => toggleOrderDetails(order._id)}
                  className="w-full py-1.5 text-[11px] font-medium text-purple-600 bg-purple-50/50 hover:bg-purple-50 rounded transition-colors border border-purple-100/50"
                >
                  {expandedOrder === order._id ? 'Hide Details' : 'View Details'}
                </button>

                {/* Expanded Details */}
                {expandedOrder === order._id && (
                  <div className="mt-2.5 pt-2.5 border-t border-gray-100 space-y-2.5 animate-fadeIn">
                    <div className="space-y-1.5">
                      {order.orderItems?.map((item, i) => (
                        <div key={i} className="flex gap-2 items-center justify-between text-xs">
                          <div className="flex gap-2 items-center min-w-0">
                            <div className="w-8 h-8 border border-gray-100 rounded bg-white overflow-hidden flex-shrink-0">
                              <img src={item.image || 'https://via.placeholder.com/32'} alt="" className="w-full h-full object-contain" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-[11px] font-medium text-gray-700 truncate">{item.name}</p>
                              <p className="text-[10px] text-gray-400">Qty: {item.quantity} × {formatPrice(item.price)}</p>
                            </div>
                          </div>
                          <span className="text-[11px] font-medium text-gray-600 flex-shrink-0">{formatPrice(item.quantity * item.price)}</span>
                        </div>
                      ))}
                    </div>

                    {order.shippingAddress && (
                      <div className="p-2 bg-gray-50 rounded border border-gray-100 text-[11px]">
                        <p className="font-medium text-gray-600 mb-0.5">Shipping Address</p>
                        <p className="text-gray-500 leading-relaxed">
                          {order.shippingAddress?.street}, {order.shippingAddress?.city}, {order.shippingAddress?.state} - {order.shippingAddress?.zipCode}
                        </p>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-2 text-[11px] border-b border-gray-100 pb-2">
                      <div>
                        <p className="text-gray-400">Payment Method</p>
                        <p className="font-medium text-gray-600">{order.paymentMethod || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-400">Payment Status</p>
                        <p className={`font-medium ${order.isPaid ? 'text-green-600' : 'text-amber-600'}`}>
                          {order.isPaid ? 'Paid' : 'Pending'}
                        </p>
                      </div>
                    </div>

                    <div className="text-[11px] space-y-1 text-gray-500">
                      <div className="flex justify-between">
                        <span>Subtotal</span>
                        <span className="text-gray-700">{formatPrice(order.itemsPrice)}</span>
                      </div>
                      {order.shippingPrice > 0 && (
                        <div className="flex justify-between">
                          <span>Shipping</span>
                          <span className="text-gray-700">{formatPrice(order.shippingPrice)}</span>
                        </div>
                      )}
                      {order.taxPrice > 0 && (
                        <div className="flex justify-between">
                          <span>Tax</span>
                          <span className="text-gray-700">{formatPrice(order.taxPrice)}</span>
                        </div>
                      )}
                      {order.couponDiscount > 0 && (
                        <div className="flex justify-between text-green-600">
                          <span>Discount</span>
                          <span>-{formatPrice(order.couponDiscount)}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-semibold text-xs text-purple-600 pt-0.5 border-t border-dashed border-gray-200">
                        <span>Total Paid</span>
                        <span>{formatPrice(order.totalPrice)}</span>
                      </div>
                    </div>

                    {order.status === 'Delivered' && (
                      <div className="pt-1">
                        {order.returnRequested ? (
                          <div className={`p-2 rounded border text-center text-[11px] ${
                            order.returnStatus === 'approved' ? 'bg-green-50 text-green-600 border-green-100' :
                            order.returnStatus === 'rejected' ? 'bg-red-50 text-red-600 border-red-100' :
                            'bg-amber-50 text-amber-600 border-amber-100'
                          }`}>
                            <p className="font-medium">
                              {order.returnStatus === 'requested' && 'Return Pending Approval'}
                              {order.returnStatus === 'approved' && 'Return Approved'}
                              {order.returnStatus === 'rejected' && 'Return Rejected'}
                            </p>
                            {order.returnReason && <p className="text-[10px] text-gray-400 mt-0.5">Reason: {order.returnReason}</p>}
                          </div>
                        ) : (
                          <button
                            onClick={() => handleReturnClick(order)}
                            className="w-full py-1.5 bg-orange-50 hover:bg-orange-100 text-orange-600 border border-orange-200/60 rounded font-medium flex items-center justify-center gap-1 transition-colors text-[11px]"
                          >
                            <RotateCcw size={12} />
                            Request Return
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Return Modal */}
      {showReturnModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-xs w-full p-3 border border-gray-100 shadow-xl">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-xs font-bold text-gray-800">Request Return</h2>
              <button onClick={() => setShowReturnModal(false)} className="p-1 hover:bg-gray-50 rounded">
                <X size={14} className="text-gray-400" />
              </button>
            </div>
            
            <form onSubmit={handleReturnSubmit} className="space-y-2.5">
              <div>
                <textarea
                  value={returnReason}
                  onChange={(e) => setReturnReason(e.target.value)}
                  placeholder="Reason for return..."
                  className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:border-purple-500 placeholder:text-gray-300"
                  rows={2.5}
                  required
                />
              </div>

              <div className="flex gap-1.5 text-xs font-medium">
                <button
                  type="button"
                  onClick={() => setShowReturnModal(false)}
                  className="flex-1 py-1.5 border border-gray-200 text-gray-500 rounded hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={returnLoading}
                  className="flex-1 py-1.5 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors disabled:opacity-50"
                >
                  {returnLoading ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// My Addresses Component
export const MyAddressesContent = ({ onBack }) => {
  const { user } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [coordinates, setCoordinates] = useState(null);
  const [formData, setFormData] = useState({ address: '', city: '', state: '', pincode: '', phone: '' });
  const navigate = useNavigate();


  useEffect(() => {
    if (user?.address) {
      setAddresses([{ address: user.address, pincode: user.pincode, phone: user.phone }]);
    }
  }, [user]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const addressString = `${formData.address}, ${formData.city}, ${formData.state} - ${formData.pincode}`;
      const resp = await userService.updateProfile({
        address: addressString,
        pincode: formData.pincode,
        phone: formData.phone
      });

      // Keep AuthContext + footer UI in sync (AuthContext reads from localStorage)
      if (resp?.data) {
        localStorage.setItem('user', JSON.stringify(resp.data));
      }

      toast.success('Address saved!');
      setShowForm(false);
      window.location.reload();
    } catch (error) {
      // surface backend validation/auth errors in UI
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to save address';
      console.error('Save address failed:', error);
      toast.error(msg);
    }
  };

  const handleBack = () => {
    if (onBack) onBack();
    else navigate('/store');
  };

  return (
    <div className="h-full overflow-y-auto pb-16 bg-white">
      {/* Header */}
      <div className="sticky top-0 bg-white/90 backdrop-blur-sm border-b border-gray-100 px-3 py-2 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button onClick={handleBack} className="p-1.5 hover:bg-gray-50 rounded-md transition-colors">
              <ChevronLeft size={16} className="text-gray-600" />
            </button>
            <MapPin size={16} className="text-purple-600" />
            <h2 className="font-semibold text-sm text-gray-800">My Addresses</h2>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="text-purple-600 text-xs font-semibold hover:text-purple-700 transition-colors"
          >
            {showForm ? 'Cancel' : '+ Add'}
          </button>
        </div>
      </div>

      <div className="p-3">
        {showForm && (
          <form onSubmit={handleSubmit} className="border border-purple-100 bg-purple-50/20 rounded-lg p-2.5 mb-3 space-y-2">
            <input
              type="text"
              name="address"
              placeholder="Street Address"
              value={formData.address}
              onChange={handleInputChange}
              className="w-full px-2.5 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:border-purple-500 bg-white"
              required
            />
            <LocateViaLiveMap
              title="Address (Locate via Live Map)"
              formData={{
                street: formData.address,
                city: formData.city,
                state: formData.state,
                zipCode: formData.pincode
              }}
              onFormDataChange={(updater) => {
                const next = typeof updater === 'function' ? updater(formData) : updater;
                setFormData((prev) => ({
                  ...prev,
                  address: next.street ?? prev.address,
                  city: next.city ?? prev.city,
                  state: next.state ?? prev.state,
                  pincode: next.zipCode ?? prev.pincode
                }));
              }}
              coordinates={coordinates}
              onCoordinatesChange={setCoordinates}
              locateButtonText="Locate via Live Map"
            />

            <div className="grid grid-cols-2 gap-2">
              <input
                type="text"
                name="city"

                placeholder="City"
                value={formData.city}
                onChange={handleInputChange}
                className="px-2.5 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:border-purple-500 bg-white"
                required
              />
              <input
                type="text"
                name="state"
                placeholder="State"
                value={formData.state}
                onChange={handleInputChange}
                className="px-2.5 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:border-purple-500 bg-white"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="text"
                name="pincode"
                placeholder="Pincode"
                value={formData.pincode}
                onChange={handleInputChange}
                className="px-2.5 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:border-purple-500 bg-white"
                required
              />
              <input
                type="tel"
                name="phone"
                placeholder="Phone (Optional)"
                value={formData.phone}
                onChange={handleInputChange}
                className="px-2.5 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:border-purple-500 bg-white"
              />
            </div>
            <button
              type="submit"
              className="w-full bg-purple-600 hover:bg-purple-700 text-white py-1.5 rounded text-xs font-medium transition-colors"
            >
              Save Address
            </button>
          </form>
        )}

        {addresses.length === 0 && !showForm ? (
          <div className="text-center py-6">
            <MapPin size={36} className="mx-auto text-gray-300 mb-2" />
            <p className="text-xs text-gray-400">No addresses saved</p>
          </div>
        ) : (
          addresses.map((addr, i) => (
            <div key={i} className="border border-gray-100 rounded-lg p-2.5 mb-2 bg-white shadow-xs">
              <p className="text-xs text-gray-700 font-medium leading-normal">{addr.address}</p>
              <div className="flex gap-3 text-[11px] text-gray-400 mt-1">
                <span>Pin: {addr.pincode}</span>
                {addr.phone && <span>Phone: {addr.phone}</span>}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Change Password Component
export const ChangePasswordContent = ({ onBack }) => {
  const [formData, setFormData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const togglePassword = (field) => setShowPasswords({ ...showPasswords, [field]: !showPasswords[field] });

  const passwordValid = formData.newPassword.length >= 6;
  const passwordsMatch = formData.newPassword === formData.confirmPassword && formData.confirmPassword.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!passwordsMatch) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await userService.changePassword({ currentPassword: formData.currentPassword, newPassword: formData.newPassword });
      toast.success('Password changed!');
      setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (onBack) onBack();
    else navigate('/store');
  };

  return (
    <div className="h-full overflow-y-auto pb-16 bg-white">
      {/* Header */}
      <div className="sticky top-0 bg-white/90 backdrop-blur-sm border-b border-gray-100 px-3 py-2 z-10">
        <div className="flex items-center gap-2">
          <button onClick={handleBack} className="p-1.5 hover:bg-gray-50 rounded-md transition-colors">
            <ChevronLeft size={16} className="text-gray-600" />
          </button>
          <Lock size={16} className="text-purple-600" />
          <h2 className="font-semibold text-sm text-gray-800">Change Password</h2>
        </div>
      </div>

      <div className="p-3 max-w-sm mx-auto">
        <form onSubmit={handleSubmit} className="space-y-3.5">
          <div className="relative">
            <input
              type={showPasswords.current ? 'text' : 'password'}
              name="currentPassword"
              placeholder="Current Password"
              value={formData.currentPassword}
              onChange={handleInputChange}
              className="w-full px-3 py-2 pr-9 border border-gray-200 rounded-lg text-xs focus:outline-none focus:border-purple-500"
              required
            />
            <button type="button" onClick={() => togglePassword('current')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              {showPasswords.current ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>

          <div className="relative">
            <input
              type={showPasswords.new ? 'text' : 'password'}
              name="newPassword"
              placeholder="New Password"
              value={formData.newPassword}
              onChange={handleInputChange}
              className="w-full px-3 py-2 pr-9 border border-gray-200 rounded-lg text-xs focus:outline-none focus:border-purple-500"
              required
            />
            <button type="button" onClick={() => togglePassword('new')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              {showPasswords.new ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
          {formData.newPassword && !passwordValid && (
            <p className="text-[10px] text-red-500 -mt-2 ml-1">Password must be at least 6 characters</p>
          )}

          <div className="relative">
            <input
              type={showPasswords.confirm ? 'text' : 'password'}
              name="confirmPassword"
              placeholder="Confirm New Password"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className="w-full px-3 py-2 pr-9 border border-gray-200 rounded-lg text-xs focus:outline-none focus:border-purple-500"
              required
            />
            <button type="button" onClick={() => togglePassword('confirm')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              {showPasswords.confirm ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
          {formData.confirmPassword && (
            <p className={`text-[10px] -mt-2 ml-1 font-medium ${passwordsMatch ? 'text-green-500' : 'text-red-500'}`}>
              {passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
            </p>
          )}

          <button
            type="submit"
            disabled={loading || !passwordsMatch}
            className="w-full py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-40"
          >
            {loading ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

// Edit Profile Component
export const EditProfileContent = ({ onBack }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: user?.address || '',
    pincode: user?.pincode || '',
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await userService.updateProfile(formData);
      toast.success('Profile updated!');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (onBack) onBack();
    else navigate('/store');
  };

  return (
    <div className="h-full overflow-y-auto pb-16 bg-white">
      {/* Header */}
      <div className="sticky top-0 bg-white/90 backdrop-blur-sm border-b border-gray-100 px-3 py-2 z-10">
        <div className="flex items-center gap-2">
          <button onClick={handleBack} className="p-1.5 hover:bg-gray-50 rounded-md transition-colors">
            <ChevronLeft size={16} className="text-gray-600" />
          </button>
          <Settings size={16} className="text-purple-600" />
          <h2 className="font-semibold text-sm text-gray-800">Edit Profile</h2>
        </div>
      </div>

      <div className="p-3 max-w-sm mx-auto">
        {/* Avatar */}
        <div className="flex justify-center mb-4">
          <div className="w-14 h-14 bg-purple-50 border border-purple-100 rounded-full flex items-center justify-center shadow-xs">
            <span className="text-lg font-bold text-purple-600">
              {formData.name ? formData.name.charAt(0).toUpperCase() : 'U'}
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="relative">
            <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              name="name"
              placeholder="Full Name"
              value={formData.name}
              onChange={handleInputChange}
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:border-purple-500"
              required
            />
          </div>

          <div className="relative">
            <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleInputChange}
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:border-purple-500"
              required
            />
          </div>

          <div className="relative">
            <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="tel"
              name="phone"
              placeholder="Phone"
              value={formData.phone}
              onChange={handleInputChange}
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:border-purple-500"
            />
          </div>

          <div className="relative">
            <MapPin size={14} className="absolute left-3 top-2.5 text-gray-400" />
            <textarea
              name="address"
              placeholder="Address"
              value={formData.address}
              onChange={handleInputChange}
              rows={2}
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:border-purple-500 resize-none"
            />
          </div>

          <input
            type="text"
            name="pincode"
            placeholder="Pincode"
            value={formData.pincode}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:border-purple-500"
          />

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default { MyOrdersContent, MyAddressesContent, ChangePasswordContent, EditProfileContent };